import { makeAutoObservable, remove, set, toJS } from 'mobx';
import { TagCondition, createTagCondition } from '../TagCondition';
import { WorkoutPhaseTemplate, createWorkoutPhaseTemplate } from './WorkoutPhaseTemplate';
import { v4 as UUID } from 'uuid';
import { logger } from '../../../../Utils/logger';

export type PhaseConfiguration = {
  id: string;
  tagConditions: TagCondition[];
  phaseTemplates: WorkoutPhaseTemplate[];
  params: { [key: string]: any };
  readonly friendlyTagString: string;
  toJS(replaceId?: boolean): any;
  copy(): PhaseConfiguration;
  setParam(paramName: string, value?: any): void;
  removeParam(paramName: string): void;
  addPhaseTemplate(phaseTemplate: WorkoutPhaseTemplate, index?: number): void;
  removePhaseTemplate(phaseId: string): void;
  changePhaseTemplateIndex(phaseTemplateId: string, index: number): void;
};

export function createPhaseConfiguration(json?: any): PhaseConfiguration {
  const config: PhaseConfiguration = makeAutoObservable({
    id: json?.id ?? UUID(),
    tagConditions: (json?.tagConditions ?? []).map((t: any) => createTagCondition(t)),
    phaseTemplates: (json?.phaseTemplates ?? []).map((t: any) => createWorkoutPhaseTemplate(t)),
    params: json?.params ?? {},

    get friendlyTagString(): string {
      if (this.tagConditions.length > 0) {
        const hasEmpty = this.tagConditions.findIndex((c) => c.empty) !== -1;
        return this.tagConditions
          .filter((c) => !c.empty)
          .map((c) => c.friendlyString)
          .concat(hasEmpty ? ['<default>'] : [])
          .join(' & ');
      }
      return '<default>';
    },

    toJS(replaceId?: boolean) {
      return {
        id: replaceId ? UUID() : this.id,
        tagConditions: this.tagConditions.map((p) => p),
        phaseTemplates: this.phaseTemplates.map((p) => p.toJS(replaceId)),
        params: toJS(this.params),
      };
    },

    copy(): PhaseConfiguration {
      return createPhaseConfiguration(this.toJS(true));
    },

    setParam(paramName: string, value?: any) {
      this.params = this.params ?? {};
      set(this.params, paramName, value);
    },

    removeParam(paramName: string) {
      this.params = this.params ?? {};
      remove(this.params, paramName);
    },

    addPhaseTemplate(phaseTemplate: WorkoutPhaseTemplate, index?: number) {
      if (index !== undefined && index < this.phaseTemplates.length) {
        const insertIndex = Math.max(index, 0);
        if (insertIndex === 0) {
          this.phaseTemplates.unshift(phaseTemplate);
        } else {
          this.phaseTemplates.splice(insertIndex, 0, phaseTemplate);
        }
      } else {
        this.phaseTemplates.push(phaseTemplate);
      }
    },

    removePhaseTemplate(phaseId: string) {
      this.phaseTemplates = this.phaseTemplates.filter((p) => p.id !== phaseId);
    },

    changePhaseTemplateIndex(phaseTemplateId: string, index: number) {
      const currentIndex = this.phaseTemplates.findIndex((p) => p.id === phaseTemplateId);
      const current = this.phaseTemplates.splice(currentIndex, 1)[0];
      logger('index', phaseTemplateId, current, currentIndex, index, this.phaseTemplates.length);
      if (current) {
        if (index < this.phaseTemplates.length) {
          const insertIndex = Math.max(index, 0);
          this.phaseTemplates.splice(insertIndex, 0, current);
        } else {
          this.phaseTemplates.push(current);
        }
      }
    },
  });

  return config;
}
